import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { array, mixed, number, object, string } from 'yup';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { useApolloClient } from '@apollo/client';
import { ResourceAllocationStatus } from '~/types';
import {
  buildResourceRequest,
  buildRuleConstraintsForPeriod,
  buildScheduleRulesWithDefaultConstraint,
  excludeWeekdaysDefault,
  getResourceRequestDatesFromProjectDate,
  getScheduleTotalHours,
  roundToDecimals
} from '~/modules/resourcing/common/util';
import { ALLOCATION_PERIOD } from '~/modules/resourcing/common/components/AllocationByPeriod/components';
import { DISPLAY_UNIT_ENUM } from '~/modules/resourcing/common/enums';
import { getHoursValueByPeriod } from '~/modules/resourcing/common/components/AllocationByPeriod/AllocationByPeriod';
import {
  dateToMidnightUTCObject,
  dateToMidnightUTCString,
  mapIsoStringtoUtcObject,
  mapRepliconDateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { compareISOStrings } from '~/modules/common/dates/compare';
import { makeTenantScopedUri } from '~/util';
import { useMeContext } from '~/modules/me';
import { useGetScheduledRulesForUserAndDateRangeCallback } from '~/modules/resourcing/common/hooks/useGetScheduleRulesForUserAndDateRange';
import { useGetScheduledHoursForUserAndDateRangeCallback } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import {
  GET_EXISTING_ALLOCATION_QUERY,
  GET_EXISTING_ALLOCATION_QUERY_V2
} from '~/modules/resourcing/common/hooks/useGetExistingAllocation';

const DEFAULT_PLANNING_LIMIT_YEARS = 2;
const MIN_LOAD = 0;
const MAX_LOAD = 999;
const MIN_TOTAL_ALLOCATED_HOURS = 0;

export const PERIOD_HOURS_FIELD_MAP = {
  [ALLOCATION_PERIOD.ENTIRE_PERIOD]: 'hoursForEntirePeriod',
  [ALLOCATION_PERIOD.MONTH]: 'hoursPerMonth',
  [ALLOCATION_PERIOD.WEEK]: 'hoursPerWeek'
};

export const getMaxDateForAllocation = ({
  startDate,
  endDate,
  resourcePlanningLimitInYears
}) => {
  const maxEndDate = startDate.plus({
    years: resourcePlanningLimitInYears || DEFAULT_PLANNING_LIMIT_YEARS
  });

  return endDate > maxEndDate ? maxEndDate : endDate;
};

const getResourceAllocationInitialState = ({
  me,
  scale,
  project,
  savedShowAdvancedSettings = false,
  savedSelectedAllocationPeriod,
  savedDisplayUnit
}) => {
  const { resourcePlanningLimitInYears } = me;
  const {
    requestStartDate: start,
    requestEndDate: end
  } = getResourceRequestDatesFromProjectDate({
    projectStartDate: project.startDate,
    projectEndDate: project.endDate,
    scale
  });

  return {
    resourceAllocation: {
      id: makeTenantScopedUri(me, 'psa-resource-allocation', uuidv4()),
      role: null,
      project,
      isNewAllocation: true
    },
    project,
    user: null,
    role: null,
    load: 100,
    startDate: dateToMidnightUTCString(start),
    endDate: dateToMidnightUTCString(
      getMaxDateForAllocation({
        startDate: start,
        endDate: end,
        resourcePlanningLimitInYears
      })
    ),
    totalAllocatedHours: 0,
    scheduleRules: [],
    showAdvancedSettings: savedShowAdvancedSettings,
    selectedAllocationPeriod:
      savedSelectedAllocationPeriod || ALLOCATION_PERIOD.ENTIRE_PERIOD,
    hoursForEntirePeriod: 0,
    percentageForEntirePeriod: 100,
    hoursPerMonth: '',
    hoursPerWeek: '',
    displayUnit: savedDisplayUnit || DISPLAY_UNIT_ENUM.HOURS
  };
};

export const getExistingResourceAllocation = async ({
  apolloClient,
  projectUri,
  userUri,
  useResourceAllocationsV2Query = false
}) => {
  const { data } = await apolloClient.query({
    query: useResourceAllocationsV2Query
      ? GET_EXISTING_ALLOCATION_QUERY_V2
      : GET_EXISTING_ALLOCATION_QUERY,
    variables: {
      projectUri,
      filter: {
        users: [userUri]
      },
      allocationStatusList: [ResourceAllocationStatus.Committed],
      consistentRead: true
    },
    fetchPolicy: 'network-only'
  });

  return data?.resourceAllocations?.resourceAllocations[0] || null;
};

const getQuickAllocationInitialState = ({
  user,
  projectRole,
  totalScheduledHours,
  startDate,
  endDate,
  scheduleRules,
  savedShowAdvancedSettings = false,
  savedSelectedAllocationPeriod,
  savedDisplayUnit
}) => ({
  id: uuidv4(),
  isNew: true,
  project: null,
  role: projectRole,
  load: 100,
  user,
  startDate,
  endDate,
  totalAllocatedHours: totalScheduledHours || 0,
  scheduleRules,
  showAdvancedSettings: savedShowAdvancedSettings,
  selectedAllocationPeriod:
    savedSelectedAllocationPeriod || ALLOCATION_PERIOD.ENTIRE_PERIOD,
  hoursForEntirePeriod: totalScheduledHours || 0,
  percentageForEntirePeriod: 100,
  hoursPerMonth: '',
  hoursPerWeek: '',
  displayUnit: savedDisplayUnit || DISPLAY_UNIT_ENUM.HOURS
});

const allocationPeriodFieldSchema = period =>
  mixed().when(['selectedAllocationPeriod', 'showAdvancedSettings'], {
    is: (selectedAllocationPeriod, showAdvancedSettings) =>
      selectedAllocationPeriod === period && showAdvancedSettings,
    then: schema =>
      schema.test('allocationHours', function validate(v) {
        const hasValue = Boolean(v) && Number(v) > 0;

        if (hasValue) return true;

        const hasNoWorkHours =
          period === ALLOCATION_PERIOD.ENTIRE_PERIOD &&
          this.parent.displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE &&
          this.parent.percentageForEntirePeriod > 0;

        return this.createError({
          message: hasNoWorkHours
            ? 'allocationByPeriod.noWorkHoursForSelectedDatesError'
            : 'allocationByPeriod.enterAnAmountError'
        });
      })
  });

const validationSchema = ({ intl }) =>
  object().shape({
    project: object().nullable(),
    role: object().nullable(),
    totalAllocatedHours: mixed().when('showAdvancedSettings', {
      is: false,
      then: () =>
        number().moreThan(
          MIN_TOTAL_ALLOCATED_HOURS,
          intl.formatMessage(
            {
              id: 'resourceRequestDrawerDetails.minValueForTotalHours'
            },
            {
              value: MIN_TOTAL_ALLOCATED_HOURS
            }
          )
        )
    }),
    load: mixed().when('showAdvancedSettings', {
      is: false,
      then: () =>
        number()
          .nullable()
          .required(
            intl.formatMessage({
              id: 'resourceRequestDrawerDetails.loadError'
            })
          )
          .moreThan(
            MIN_LOAD,
            intl.formatMessage(
              {
                id: 'resourceRequestDrawerDetails.minValueForLoad'
              },
              {
                value: MIN_LOAD
              }
            )
          )
          .max(
            MAX_LOAD,
            intl.formatMessage(
              {
                id: 'resourceRequestDrawerDetails.maxValueForLoad'
              },
              {
                value: MAX_LOAD
              }
            )
          )
    }),
    startDate: string(),
    endDate: string(),
    user: object().nullable(),
    scheduleRules: array().nullable(),
    hoursForEntirePeriod: allocationPeriodFieldSchema(
      ALLOCATION_PERIOD.ENTIRE_PERIOD
    ),
    hoursPerMonth: allocationPeriodFieldSchema(ALLOCATION_PERIOD.MONTH),
    hoursPerWeek: allocationPeriodFieldSchema(ALLOCATION_PERIOD.WEEK)
  });

const getUpdatedScheduleRules = ({
  totalAllocatedHours,
  totalScheduledHours,
  scheduleRules,
  startDate,
  endDate
}) => {
  const dayFactor = totalAllocatedHours / totalScheduledHours;

  const totalDays =
    DateTime.fromISO(endDate)
      .diff(DateTime.fromISO(startDate), 'days')
      .toObject().days + 1;

  return scheduleRules.map(rule => ({
    ...rule,
    do: {
      ...rule.do,
      load:
        totalScheduledHours > 0
          ? 100 * dayFactor
          : totalAllocatedHours / totalDays
    }
  }));
};

const getUpdatedScheduleRulesWithScheduleRuleConstraint = ({
  scheduleRules,
  load,
  startDate,
  endDate,
  totalAllocatedHours
}) => {
  const scheduledHours = getScheduleTotalHours({
    scheduleRules,
    quantity: 1
  });

  return scheduledHours !== 0
    ? getUpdatedScheduleRules({
        totalAllocatedHours: (scheduledHours * load) / 100,
        scheduleRules,
        startDate,
        endDate,
        totalScheduledHours: scheduledHours
      })
    : buildScheduleRulesWithDefaultConstraint({
        startDate,
        endDate,
        defaultScheduleRule: {
          do: buildRuleConstraintsForPeriod({
            start: dateToMidnightUTCObject(startDate),
            end: dateToMidnightUTCObject(endDate),
            quantity: 1,
            totalHours: totalAllocatedHours,
            excludeWeekdays: excludeWeekdaysDefault,
            load: load || 100
          })
        }
      });
};

export const useAllocateResourcePopupHandler = ({
  scale,
  handleAddAllocation,
  project,
  setNewDraftResourceAllocation,
  user,
  projectRole,
  defaultDates,
  totalScheduledHours,
  scheduleRules,
  savedShowAdvancedSettings = false,
  savedSelectedAllocationPeriod,
  savedDisplayUnit
}) => {
  const intl = useIntl();
  const me = useMeContext();

  const initialValues = project
    ? getResourceAllocationInitialState({
        me,
        scale,
        project,
        savedShowAdvancedSettings,
        savedSelectedAllocationPeriod,
        savedDisplayUnit
      })
    : getQuickAllocationInitialState({
        user,
        projectRole,
        startDate: defaultDates.startDate,
        endDate: defaultDates.endDate,
        totalScheduledHours,
        scheduleRules,
        savedShowAdvancedSettings,
        savedSelectedAllocationPeriod,
        savedDisplayUnit
      });

  const onResourceAdd = useCallback(
    value => {
      const scheduledHours = getScheduleTotalHours({
        scheduleRules: value.scheduleRules,
        quantity: 1
      });

      const updateScheduledRules = getUpdatedScheduleRulesWithScheduleRuleConstraint(
        {
          scheduleRules: value.scheduleRules,
          totalAllocatedHours: value.totalAllocatedHours,
          startDate: value.startDate,
          endDate: value.endDate,
          load: value.load
        }
      );

      handleAddAllocation({
        resourceAllocation: value.resourceAllocation,
        user: value.user,
        role: value.role,
        load:
          scheduledHours !== 0
            ? (value.totalAllocatedHours / scheduledHours) * 100
            : 100,
        startDate: value.startDate,
        endDate: value.endDate,
        scheduleRules: updateScheduledRules,
        totalAllocatedHours: value.showAdvancedSettings
          ? parseFloat(
              getHoursValueByPeriod({
                selectedAllocationPeriod: value.selectedAllocationPeriod,
                hoursForEntirePeriod: value.hoursForEntirePeriod,
                hoursPerMonth: value.hoursPerMonth,
                hoursPerWeek: value.hoursPerWeek
              })
            ) || 0
          : value.totalAllocatedHours,
        periodType: value.showAdvancedSettings
          ? value.selectedAllocationPeriod
          : ALLOCATION_PERIOD.ENTIRE_PERIOD,
        allocationDateRange: {
          startDate: value.startDate,
          endDate: value.endDate
        }
      });

      return {
        ...value,
        isChanged: false
      };
    },
    [handleAddAllocation]
  );

  const onQuickAllocationAdd = useCallback(
    async value => {
      const req = buildResourceRequest({
        project: {
          ...value.project
        },
        scale,
        me
      });

      const updateScheduledRules = getUpdatedScheduleRulesWithScheduleRuleConstraint(
        {
          scheduleRules: value.scheduleRules,
          totalAllocatedHours: value.totalAllocatedHours,
          startDate: value.startDate,
          endDate: value.endDate,
          load: value.load
        }
      );

      setNewDraftResourceAllocation({
        ...req,
        user,
        project: value.project,
        isNew: true,
        roleUri: value.role && value.role.id,
        scheduleRules: updateScheduledRules,
        totalAllocatedHours: value.showAdvancedSettings
          ? parseFloat(
              getHoursValueByPeriod({
                selectedAllocationPeriod: value.selectedAllocationPeriod,
                hoursForEntirePeriod: value.hoursForEntirePeriod,
                hoursPerMonth: value.hoursPerMonth,
                hoursPerWeek: value.hoursPerWeek
              })
            ) || 0
          : value.totalAllocatedHours,
        periodType: value.showAdvancedSettings
          ? value.selectedAllocationPeriod
          : ALLOCATION_PERIOD.ENTIRE_PERIOD,
        allocationDateRange: {
          startDate: value.startDate,
          endDate: value.endDate
        }
      });

      return {
        ...value,
        isChanged: false
      };
    },
    [scale, me, setNewDraftResourceAllocation, user]
  );

  return {
    initialValues,
    validationSchema: validationSchema({ intl }),
    handleAdd: project ? onResourceAdd : onQuickAllocationAdd
  };
};

export const useAllocateResourcePopupFormChange = ({
  setFieldValue,
  setFieldTouched,
  setValues,
  scale,
  values,
  fetchProjectDetails,
  setTotalScheduledHours,
  totalScheduledHours,
  setExistingResourceAllocation,
  isPsaRmpTaskAllocation1Enabled,
  putAllocateResourceDialogSettings,
  useResourceAllocationsV2Query
}) => {
  const {
    getUserScheduleRulesForDateRange
  } = useGetScheduledRulesForUserAndDateRangeCallback();
  const {
    getScheduleHoursForUserAndDateRange
  } = useGetScheduledHoursForUserAndDateRangeCallback();
  const apolloClient = useApolloClient();
  const { resourcePlanningLimitInYears } = useMeContext();

  return {
    onRoleUpdate: useCallback(
      value => {
        setFieldValue('role', value);
      },
      [setFieldValue]
    ),
    onProjectUpdate: useCallback(
      async value => {
        if (!value) {
          setValues({
            ...values,
            project: null
          });

          isPsaRmpTaskAllocation1Enabled && setExistingResourceAllocation(null);

          return;
        }
        const response = value?.id && (await fetchProjectDetails(value.id));

        if (isPsaRmpTaskAllocation1Enabled) {
          if (values.user?.uri && value?.id) {
            const existingResourceAllocation = await getExistingResourceAllocation(
              {
                apolloClient,
                projectUri: value.id,
                userUri: values.user.uri,
                useResourceAllocationsV2Query
              }
            );

            setExistingResourceAllocation(existingResourceAllocation);
          } else setExistingResourceAllocation(null);
        }
        const {
          requestStartDate: allocationStartDate,
          requestEndDate: endDate
        } = getResourceRequestDatesFromProjectDate({
          projectStartDate: response?.data?.project?.startDate,
          projectEndDate: response?.data?.project?.endDate,
          scale,
          outputDateTransformFn: date => date
        });
        const allocationEndDate = dateToMidnightUTCString(
          getMaxDateForAllocation({
            startDate: mapIsoStringtoUtcObject(allocationStartDate),
            endDate: mapIsoStringtoUtcObject(endDate),
            resourcePlanningLimitInYears
          })
        );

        if (value) {
          if (isPsaRmpTaskAllocation1Enabled) {
            const {
              scheduledHours
            } = await getScheduleHoursForUserAndDateRange(values.user.uri, {
              startDate: allocationStartDate,
              endDate: allocationEndDate
            });

            setTotalScheduledHours(scheduledHours || 0);
            setFieldValue(
              'totalAllocatedHours',
              scheduledHours > 0 ? (values.load * scheduledHours) / 100 : 0
            );
            setFieldValue(
              'hoursForEntirePeriod',
              scheduledHours > 0
                ? (values.percentageForEntirePeriod * scheduledHours) / 100
                : 0
            );
          } else {
            const userScheduleRulesForDateRange = await getUserScheduleRulesForDateRange(
              values.user.uri,
              {
                startDate: allocationStartDate,
                endDate: allocationEndDate
              }
            );

            setTotalScheduledHours(
              userScheduleRulesForDateRange?.scheduledHours
            );
            setFieldValue(
              'scheduleRules',
              userScheduleRulesForDateRange?.scheduleRules
            );
            setFieldValue(
              'totalAllocatedHours',
              userScheduleRulesForDateRange?.scheduledHours > 0
                ? (values.load *
                    userScheduleRulesForDateRange?.scheduledHours) /
                    100
                : 0
            );
          }
        }
        setFieldValue('startDate', allocationStartDate);
        setFieldValue('endDate', allocationEndDate);
        setFieldValue('project', response?.data?.project);
      },

      [
        apolloClient,
        fetchProjectDetails,
        getUserScheduleRulesForDateRange,
        getScheduleHoursForUserAndDateRange,
        isPsaRmpTaskAllocation1Enabled,
        useResourceAllocationsV2Query,
        resourcePlanningLimitInYears,
        scale,
        setExistingResourceAllocation,
        setFieldValue,
        setTotalScheduledHours,
        setValues,
        values
      ]
    ),
    onUserUpdateForTaskAllocation: useCallback(
      async value => {
        setFieldValue('user', value);

        if (value && values.project?.id) {
          const existingResourceAllocation = await getExistingResourceAllocation(
            {
              apolloClient,
              projectUri: values.project.id,
              userUri: value.uri,
              useResourceAllocationsV2Query
            }
          );

          setExistingResourceAllocation(existingResourceAllocation);
        } else setExistingResourceAllocation(null);

        if (value) {
          if (isPsaRmpTaskAllocation1Enabled) {
            const {
              scheduledHours
            } = await getScheduleHoursForUserAndDateRange(value.uri, {
              startDate: values.startDate,
              endDate: values.endDate
            });

            setTotalScheduledHours(scheduledHours || 0);
            setFieldValue('totalAllocatedHours', scheduledHours || 0);
            setFieldValue('load', 100);
            setFieldValue('hoursForEntirePeriod', scheduledHours || 0);
            setFieldValue('percentageForEntirePeriod', 100);
          } else {
            const response = await getUserScheduleRulesForDateRange(value.uri, {
              startDate: values.startDate,
              endDate: values.endDate
            });

            setTotalScheduledHours(response?.scheduledHours);
            setFieldValue('scheduleRules', response?.scheduleRules);
            setFieldValue('totalAllocatedHours', response?.scheduledHours || 0);
            setFieldValue('load', 100);
          }
        }

        if (value && value.roles && !values.role) {
          const primaryUserRole = value.roles?.find(r => r.isPrimary);

          primaryUserRole &&
            setFieldValue('role', {
              displayText: primaryUserRole.projectRole.name,
              id: primaryUserRole.projectRole.uri
            });
        }
      },
      [
        setFieldValue,
        setExistingResourceAllocation,
        getUserScheduleRulesForDateRange,
        getScheduleHoursForUserAndDateRange,
        isPsaRmpTaskAllocation1Enabled,
        useResourceAllocationsV2Query,
        values.startDate,
        values.endDate,
        values.role,
        values.project?.id,
        setTotalScheduledHours,
        apolloClient
      ]
    ),
    onUserUpdate: useCallback(
      async value => {
        setFieldValue('user', value);

        if (value) {
          if (isPsaRmpTaskAllocation1Enabled) {
            const {
              scheduledHours
            } = await getScheduleHoursForUserAndDateRange(value.uri, {
              startDate: values.startDate,
              endDate: values.endDate
            });

            setTotalScheduledHours(scheduledHours || 0);
            setFieldValue('totalAllocatedHours', scheduledHours || 0);
            setFieldValue('load', 100);
            setFieldValue('hoursForEntirePeriod', scheduledHours || 0);
            setFieldValue('percentageForEntirePeriod', 100);
          } else {
            const response = await getUserScheduleRulesForDateRange(value.uri, {
              startDate: values.startDate,
              endDate: values.endDate
            });

            setTotalScheduledHours(response?.scheduledHours);
            setFieldValue('scheduleRules', response?.scheduleRules);
            setFieldValue('totalAllocatedHours', response?.scheduledHours || 0);
            setFieldValue('load', 100);
          }
        }

        if (value && value.roles && !values.role) {
          const primaryUserRole = value.roles?.find(r => r.isPrimary);

          primaryUserRole &&
            setFieldValue('role', {
              displayText: primaryUserRole.projectRole.name,
              id: primaryUserRole.projectRole.uri
            });
        }
      },
      [
        setFieldValue,
        getUserScheduleRulesForDateRange,
        getScheduleHoursForUserAndDateRange,
        isPsaRmpTaskAllocation1Enabled,
        values.startDate,
        values.endDate,
        values.role,
        setTotalScheduledHours
      ]
    ),
    onLoadingUpdate: useCallback(
      load => {
        setValues({
          ...values,
          load,
          totalAllocatedHours:
            totalScheduledHours > 0
              ? (load * totalScheduledHours) / 100
              : values.totalAllocatedHours
        });
      },
      [setValues, totalScheduledHours, values]
    ),
    onTotalAllocatedHoursUpdate: useCallback(
      value =>
        setValues({
          ...values,
          load:
            totalScheduledHours > 0
              ? (value / totalScheduledHours) * 100
              : values.load,
          totalAllocatedHours: value
        }),
      [setValues, totalScheduledHours, values]
    ),
    onStartDateUpdate: useCallback(
      async startDate => {
        const formattedStartDate = mapRepliconDateToMidnightUTCString(
          startDate
        );

        const isStartDateAfterEndDate =
          compareISOStrings(formattedStartDate, values.endDate) === 1;

        if (!values.user) {
          if (isStartDateAfterEndDate) {
            setValues({
              ...values,
              endDate: formattedStartDate,
              startDate: formattedStartDate
            });

            return;
          }

          setValues({
            ...values,
            startDate: formattedStartDate
          });

          return;
        }
        const endDateForQuery = isStartDateAfterEndDate
          ? formattedStartDate
          : values.endDate;

        if (isPsaRmpTaskAllocation1Enabled) {
          const {
            scheduledHours: totalHours
          } = await getScheduleHoursForUserAndDateRange(values.user.uri, {
            startDate: formattedStartDate,
            endDate: endDateForQuery
          });

          setTotalScheduledHours(totalHours || 0);
          if (isStartDateAfterEndDate) {
            setValues({
              ...values,
              endDate: formattedStartDate,
              totalAllocatedHours: totalHours || 0,
              hoursForEntirePeriod: totalHours || 0,
              percentageForEntirePeriod: 100,
              startDate: formattedStartDate
            });

            return;
          }

          setValues({
            ...values,
            totalAllocatedHours:
              totalHours > 0 ? (totalHours * values.load) / 100 : 0,
            hoursForEntirePeriod:
              totalHours > 0
                ? (totalHours * values.percentageForEntirePeriod) / 100
                : 0,
            startDate: formattedStartDate
          });
        } else {
          const {
            scheduledHours: totalHours,
            scheduleRules
          } = await getUserScheduleRulesForDateRange(values.user.uri, {
            startDate: formattedStartDate,
            endDate: endDateForQuery
          });

          setTotalScheduledHours(totalHours);
          if (isStartDateAfterEndDate) {
            setValues({
              ...values,
              scheduleRules,
              endDate: formattedStartDate,
              totalAllocatedHours: totalHours,
              startDate: formattedStartDate
            });

            return;
          }

          setValues({
            ...values,
            scheduleRules,
            totalAllocatedHours:
              totalHours > 0 ? (totalHours * values.load) / 100 : 0,
            startDate: formattedStartDate
          });
        }
      },
      [
        getUserScheduleRulesForDateRange,
        getScheduleHoursForUserAndDateRange,
        isPsaRmpTaskAllocation1Enabled,
        setTotalScheduledHours,
        setValues,
        values
      ]
    ),
    onEndDateUpdate: useCallback(
      async endDate => {
        const formattedEndDate = mapRepliconDateToMidnightUTCString(endDate);
        const isEndDateBeforeStartDate =
          compareISOStrings(formattedEndDate, values.startDate) === -1;

        if (!values.user) {
          if (isEndDateBeforeStartDate) {
            setValues({
              ...values,
              endDate: formattedEndDate,
              startDate: formattedEndDate
            });

            return;
          }

          setValues({
            ...values,
            endDate: formattedEndDate
          });

          return;
        }
        const startDateForQuery = isEndDateBeforeStartDate
          ? formattedEndDate
          : values.startDate;

        if (isPsaRmpTaskAllocation1Enabled) {
          const {
            scheduledHours: totalHours
          } = await getScheduleHoursForUserAndDateRange(values.user.uri, {
            startDate: startDateForQuery,
            endDate: formattedEndDate
          });

          if (isEndDateBeforeStartDate) {
            setValues({
              ...values,
              endDate: formattedEndDate,
              totalAllocatedHours: totalHours || 0,
              hoursForEntirePeriod: totalHours || 0,
              percentageForEntirePeriod: 100,
              startDate: formattedEndDate
            });

            return;
          }

          setTotalScheduledHours(totalHours || 0);
          setValues({
            ...values,
            endDate: formattedEndDate,
            totalAllocatedHours:
              totalHours > 0 ? (totalHours * values.load) / 100 : 0,
            hoursForEntirePeriod:
              totalHours > 0
                ? (totalHours * values.percentageForEntirePeriod) / 100
                : 0
          });
        } else {
          const {
            scheduledHours: totalHours,
            scheduleRules
          } = await getUserScheduleRulesForDateRange(values.user.uri, {
            startDate: startDateForQuery,
            endDate: formattedEndDate
          });

          if (isEndDateBeforeStartDate) {
            setValues({
              ...values,
              scheduleRules,
              endDate: formattedEndDate,
              totalAllocatedHours: totalHours,
              startDate: formattedEndDate
            });

            return;
          }

          setTotalScheduledHours(totalHours);
          setValues({
            ...values,
            scheduleRules,
            endDate: formattedEndDate,
            totalAllocatedHours:
              totalHours > 0 ? (totalHours * values.load) / 100 : 0
          });
        }
      },
      [
        getUserScheduleRulesForDateRange,
        getScheduleHoursForUserAndDateRange,
        isPsaRmpTaskAllocation1Enabled,
        setTotalScheduledHours,
        setValues,
        values
      ]
    ),
    onSelectedAllocationPeriodChange: useCallback(
      (_, selectedTab) => {
        setFieldValue('selectedAllocationPeriod', selectedTab);
        putAllocateResourceDialogSettings?.({
          showAdvancedSettings: values.showAdvancedSettings,
          selectedAllocationPeriod: selectedTab,
          displayUnit: values.displayUnit
        });
      },
      [
        setFieldValue,
        putAllocateResourceDialogSettings,
        values.showAdvancedSettings,
        values.displayUnit
      ]
    ),
    onDisplayUnitChange: useCallback(
      newDisplayUnit => {
        setFieldValue('displayUnit', newDisplayUnit);
        putAllocateResourceDialogSettings?.({
          showAdvancedSettings: values.showAdvancedSettings,
          selectedAllocationPeriod: values.selectedAllocationPeriod,
          displayUnit: newDisplayUnit
        });
      },
      [
        setFieldValue,
        putAllocateResourceDialogSettings,
        values.showAdvancedSettings,
        values.selectedAllocationPeriod
      ]
    ),
    onHoursValueChange: useCallback(
      hours => {
        const { selectedAllocationPeriod } = values;
        const fieldName = PERIOD_HOURS_FIELD_MAP[selectedAllocationPeriod];

        setFieldTouched(fieldName, true, false);

        if (
          selectedAllocationPeriod === ALLOCATION_PERIOD.ENTIRE_PERIOD &&
          totalScheduledHours > 0
        ) {
          const percentage = (hours / totalScheduledHours) * 100;

          setValues({
            ...values,
            hoursForEntirePeriod: hours,
            percentageForEntirePeriod: percentage
          });
        } else {
          setFieldValue(fieldName, hours);
        }
      },
      [setFieldTouched, setFieldValue, setValues, totalScheduledHours, values]
    ),
    onPercentageValueChange: useCallback(
      percentage => {
        setFieldTouched('hoursForEntirePeriod', true, false);

        if (totalScheduledHours > 0) {
          const hours = roundToDecimals(
            (percentage / 100) * totalScheduledHours
          );

          setValues({
            ...values,
            percentageForEntirePeriod: percentage,
            hoursForEntirePeriod: hours
          });
        } else {
          setFieldValue('percentageForEntirePeriod', percentage);
        }
      },
      [setFieldTouched, setFieldValue, setValues, totalScheduledHours, values]
    )
  };
};

export default useAllocateResourcePopupHandler;
